import {define, inject} from '@injex/core';
import {makeObservable, observable} from 'mobx';
import {ColumnsManager} from '@vidazoo/ui-kit';
import {DataListQuery} from '../../../../../common/models/DataListQuery';
import {IUserNotificationSetting} from '../../../interfaces/IUserNotificationSetting';
import {UserNotificationsManager} from '../../../managers/userNotificationsManager.mdl';
import {SessionManager} from '../../../../session/managers/sessionManager.mdl';
import {UserNotificationSettingsTableColumns} from '../common/constants';
import {AlertsManager} from '../../../../../common/alerts/managers/alertsManager.mdl';
import {AlertAccent} from '../../../../../common/alerts/common/enums';

@define()
export class NotificationSettingsModel extends ColumnsManager<IUserNotificationSetting> {
    @inject() private userNotificationsManager: UserNotificationsManager;
    @inject() private sessionManager: SessionManager;
    @inject() public alertsManager: AlertsManager;
    public items: DataListQuery<IUserNotificationSetting, []>;
    @observable public pendingIds: string[] = [];
    @observable public actionError = '';
    @observable public promptDeleteItem: IUserNotificationSetting = null;

    constructor() {
        super({persistKey: 'notification_settings_list_classic_columns'});
        this.setColumns(UserNotificationSettingsTableColumns);
        this.items = new DataListQuery(this._fetchItems.bind(this), {defaultValue: [], dataObservType: observable.ref});
        makeObservable(this);
    }
    public get data(): IUserNotificationSetting[] {return this.items.data || [];}
    public get isLoading() {return this.items.isLoading;}
    public get phone(): string {
        return (this.sessionManager.user as any)?.phone || this.sessionManager.auth0User?.phone_number || this.sessionManager.auth0User?.user_metadata?.phone || '';
    }
    private get socket() {return this.userNotificationsManager.socketManager.userNotifications;}
    private async _fetchItems() {
        const results = await this.socket.getNotificationsSettingsById(this.sessionManager.user._id) as IUserNotificationSetting[];
        return results;
    }
    public fetch = () => this.items.invalidate().fetch();

    public updateSetting = async (id: string, key: 'notification' | 'email' | 'sms', value: boolean) => {
        const item = this.data.find(row => row._id === id);
        if (!item || this.pendingIds.includes(id) || (key === 'sms' && !this.phone) || (key === 'notification' && item.type === 'custom')) return;
        this.pendingIds = [...this.pendingIds, id];
        this.actionError = '';
        try {
            await this.socket.updateSetting(id, {[key]: value});
            this.items.data = this.data.map(row => row._id === id ? {...row, [key]: value} : row);
            this.alertsManager.setBasicAlert({content: 'Setting updated successfully'}, AlertAccent.Info);
        } catch {
            this.actionError = 'Failed to update notifications settings';
            this.alertsManager.setBasicAlert({content: this.actionError}, AlertAccent.Error);
        } finally {
            this.pendingIds = this.pendingIds.filter(pending => pending !== id);
        }
    };
    public deleteItem = async () => {
        const item = this.promptDeleteItem;
        this.promptDeleteItem = null;
        if (!item || this.pendingIds.includes(item._id)) return;
        this.pendingIds = [...this.pendingIds, item._id];
        this.actionError = '';
        try {
            await this.socket.deleteSetting(item._id);
            this.items.data = this.data.filter(row => row._id !== item._id);
            this.alertsManager.setBasicAlert({content: 'Notification setting deleted successfully'}, AlertAccent.Success);
        } catch {
            this.actionError = 'Failed to delete notification setting';
            this.alertsManager.setBasicAlert({content: this.actionError}, AlertAccent.Error);
        } finally {
            this.pendingIds = this.pendingIds.filter(id => id !== item._id);
        }
    };
}
